import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { restoreAuth } from '../features/auth/authSlice';

// ProtectedRoute component with localStorage fallback to prevent random logouts
import {
    canAccessRoute,
    getDashboardRoute,
    validateAccess,
    PERMISSIONS,
    ROLES
} from '../utils/roleBasedAccess';

const ProtectedRoute = ({
    children,
    allowedRoles = [],
    requiredPermissions = [],
    requireVerification = true,
    resource = null,
    fallbackRoute = null
}) => {
    const location = useLocation();
    const { user, isVerified } = useSelector((state) => state.auth);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [cachedUser, setCachedUser] = useState(null);

    const dispatch = useDispatch();

    // Cache user from localStorage as fallback and restore auth if needed
    useEffect(() => {
        const checkLocalStorage = () => {
            try {
                const userStr = localStorage.getItem('user');
                const accessToken = localStorage.getItem('accessToken');
                
                if (userStr && accessToken) {
                    const localUser = JSON.parse(userStr);
                    setCachedUser(localUser);
                    
                    // If Redux state is empty but localStorage has data, restore it
                    if (!user) {
                        dispatch(restoreAuth());
                    }
                }
            } catch (error) {
                console.warn('Failed to parse user from localStorage:', error);
            }
            setCheckingAuth(false);
        };
        checkLocalStorage();
    }, [user, dispatch]);

    // Update cached user when Redux user changes
    useEffect(() => {
        if (user) {
            setCachedUser(user);
        }
    }, [user]);

    // Use Redux user if available, otherwise fall back to cached user
    const currentUser = user || cachedUser;

    // Show loading while checking auth (prevents flash of login page)
    if (checkingAuth) {
        return null; // or a loading spinner
    }

    // If user is not logged in (check both Redux and localStorage), redirect to login
    if (!currentUser) {
        return <Navigate to = '/login'
        state = {
            { from: location } }
        replace / > ;
    }

    // Check if user can access the current route
    if (!canAccessRoute(currentUser, location.pathname)) {
        const dashboardRoute = getDashboardRoute(currentUser);
        return <Navigate to = { dashboardRoute }
        replace / > ;
    }

    // Check if user role is allowed (legacy support)
    if (allowedRoles.length > 0 && !allowedRoles.includes(currentUser.role)) {
        const dashboardRoute = fallbackRoute || getDashboardRoute(currentUser);
        return <Navigate to = { dashboardRoute }
        replace / > ;
    }

    // Check required permissions
    if (requiredPermissions.length > 0) {
        const hasAllPermissions = requiredPermissions.every(permission => {
            const validation = validateAccess(currentUser, permission, resource);
            return validation.success;
        });

        if (!hasAllPermissions) {
            const dashboardRoute = fallbackRoute || getDashboardRoute(currentUser);
            return <Navigate to = { dashboardRoute }
            replace / > ;
        }
    }

    // Check if verification is required and user is not verified
    // Only check isVerified if user is from Redux (not cached)
    const shouldCheckVerification = requireVerification && user && isVerified !== undefined;
    if (
        shouldCheckVerification &&
        !isVerified &&
        location.pathname !== '/verify-otp'
    ) {
        return <Navigate to = '/verify-otp'
        replace / > ;
    }

    // Role-specific profile completion checks
    if (requireVerification) {
        // Doctor profile completion
        if (
            currentUser.role === ROLES.DOCTOR &&
            !currentUser.profile_completed &&
            location.pathname !== '/doctor-onboarding'
        ) {
            return <Navigate to = '/doctor-onboarding'
            replace / > ;
        }

        // Patient profile completion - check both patient_profile_completed and profile_completed
        if (
            currentUser.role === ROLES.PATIENT &&
            !currentUser.patient_profile_completed &&
            !currentUser.profile_completed &&
            !location.pathname.startsWith('/onboarding/patient') &&
            location.pathname !== '/patient-dashboard'
        ) {
            return <Navigate to = '/onboarding/patient/'
            replace / > ;
        }

        // Receptionist profile completion
        if (
            currentUser.role === ROLES.RECEPTIONIST &&
            !currentUser.profile_completed &&
            location.pathname !== '/receptionist-onboarding'
        ) {
            return <Navigate to = '/receptionist-onboarding'
            replace / > ;
        }

        // Pharmacist profile completion
        if (
            currentUser.role === ROLES.PHARMACIST &&
            !currentUser.profile_completed &&
            location.pathname !== '/pharmacist-onboarding'
        ) {
            return <Navigate to = '/pharmacist-onboarding'
            replace / > ;
        }
    }

    return children;
};

export default ProtectedRoute;